/**
 * Phil platform — Evaluator (FI) persona types and UI config.
 *
 * All data (evaluator info, assigned applicants, application details, scoring
 * questions) comes exclusively from the backend via:
 *   GET /api/fi/assigned       → { evaluator, questions, applicants }
 *   GET /api/fi/applications/{id} → ApplicationDetail
 */

export interface ScoringQuestion {
  id: number;
  title: string;
  prompt: string;
  legend: string;
}

export type ScoreStatus = 'Not Started' | 'Draft' | 'Submitted';

export interface AssignedApplicant {
  id: string;
  company: string;
  tagline: string;
  status: ScoreStatus;
  myAverage: number | null;
}

export interface PitchMaterial {
  questionId: number | null;
  label: string;
  name: string;
  contentType: string;
  key: string;
}

// Schema-driven scoring view (GET /api/fi/applications/{id}). Each active section
// is a tab; within it, rows are grouped under an optional sub-heading and rendered
// by `hint`: 'chips'/'esg' use `items`, 'cert' uses `ok`, null uses `value`.
export type ScoringHint = 'chips' | 'cert' | 'esg' | 'link' | null;

export interface ScoringRow {
  questionId: number | null;
  label: string;
  hint: ScoringHint;
  value?: string;
  items?: string[];
  ok?: boolean;
}

export interface ScoringGroup {
  subgroup: string | null;
  rows: ScoringRow[];
}

export interface ScoringSection {
  id: string;
  title: string;
  shortLabel: string;
  groups: ScoringGroup[];
}

export interface ApplicationDetail {
  id: string;
  company: string;
  tagline: string;
  sections: ScoringSection[];
  pitchMaterials?: PitchMaterial[];
}

export const SCORE_STATUS_BADGE: Record<ScoreStatus, string> = {
  'Not Started': 'phil-badge phil-badge-draft',
  Draft: 'phil-badge phil-badge-review',
  Submitted: 'phil-badge phil-badge-complete',
};
